'use client';
/**
 * 검색 v2 공용 훅 — 헤더(⌘K)·홈 히어로·게시판 글쓰기가 «같은» 제안 엔진을 쓴다(두 개의 검색 로직 금지).
 *
 * - 150ms 디바운스 → GET /api/search/suggest?q=&region= → RPC search_suggest(초성·별칭·오타·의도어는 RPC 담당).
 * - 기록(s-log · search_unify1 C): «확정» 만 1행 — 항목을 고르면(select) 그 질의를 한 번
 *   POST /api/search/v2-log → log_search_v2(source·session_id·latency_ms) → search_logs.id.
 *   입력이 멈춘 것(settle)·키 입력은 기록하지 않는다(키 입력 로그 0). Enter 는 /search 결과 페이지가 1행(SearchConfirmLogger).
 *   결과 0건이면 서버가 search_misses 에 넣는다(클라이언트가 직접 쓰지 않는다).
 * - 선택: 같은 v2-log 요청에 slug·rank → 서버가 log_search_selected(selected_slug·clicked_rank)까지.
 * - session_id: sessionStorage 난수(개인 식별 없음).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface SuggestItem {
  slug: string;
  name: string;
  region: string | null;
  sigungu: string | null;
  stage: string | null;
  score: number | null;
}

export interface SuggestOptions {
  /** search_logs.source / search_misses.source — 'header' | 'hero' | 'board_write' | 'search_page' … */
  source: string;
  /** 「지역 안에서」: regions.code(시/도 또는 시·군). null = 전국. */
  regionCode?: string | null;
  limit?: number;
  debounceMs?: number;
}

const SID_KEY = 'kd_search_sid';
export const SUGGEST_DEBOUNCE_MS = 150;
/** 옛 입력 멈춤 기록 간격(쓰지 않음 — 외부 참조 호환용 상수). */
export const SUGGEST_SETTLE_MS = 1000;

export function searchSessionId(): string {
  try {
    let v = window.sessionStorage.getItem(SID_KEY);
    if (!v) {
      v = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      window.sessionStorage.setItem(SID_KEY, v);
    }
    return v;
  } catch {
    return 'nosession';
  }
}

function post(url: string, body: unknown): Promise<Response | null> {
  return fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(() => null);
}

export function useSearchSuggest(query: string, opts: SuggestOptions) {
  const { source, regionCode = null, limit = 8, debounceMs = SUGGEST_DEBOUNCE_MS } = opts;
  const [items, setItems] = useState<SuggestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [settledQ, setSettledQ] = useState('');
  const [logId, setLogId] = useState<string | null>(null);
  // search_unify1 s-match — 제안 응답의 tier(zone_siblings·similar = 근접 안내) · zoneNote(「서금사 5·6·A구역」).
  const [meta, setMeta] = useState<{ q: string; tier: string | null; zoneNote: string | null }>({ q: '', tier: null, zoneNote: null });
  const abortRef = useRef<AbortController | null>(null);
  const lastRef = useRef<{ q: string; region: string | null; count: number; latency: number } | null>(null);
  const loggedRef = useRef<string>('');
  const logPromiseRef = useRef<Promise<string | null> | null>(null);

  const q = (query ?? '').trim();

  const commitLog = useCallback((): Promise<string | null> => {
    const last = lastRef.current;
    if (!last || !last.q) return Promise.resolve(null);
    const key = `${last.q}|${last.region ?? ''}`;
    if (loggedRef.current === key && logPromiseRef.current) return logPromiseRef.current;
    loggedRef.current = key;
    const p = post('/api/search/v2-log', {
      q: last.q, count: last.count, latencyMs: last.latency, source, sid: searchSessionId(), region: last.region,
    }).then(async (r) => {
      if (!r || !r.ok) return null;
      const j = await r.json().catch(() => null);
      const id = typeof j?.logId === 'string' ? j.logId : null;
      setLogId(id);
      return id;
    });
    logPromiseRef.current = p;
    return p;
  }, [source]);

  useEffect(() => {
    if (!q) {
      abortRef.current?.abort();
      setItems([]);
      setLoading(false);
      setSettledQ('');
      return;
    }
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      const started = performance.now();
      try {
        const u = new URLSearchParams({ q, lim: String(limit) });
        if (regionCode) u.set('region', regionCode);
        const r = await fetch(`/api/search/suggest?${u.toString()}`, { signal: ac.signal });
        const j = r.ok ? await r.json() : { items: [] };
        const list: SuggestItem[] = Array.isArray(j?.items) ? j.items : [];
        setItems(list);
        setMeta({ q, tier: typeof j?.tier === 'string' ? j.tier : null, zoneNote: typeof j?.zoneNote === 'string' ? j.zoneNote : null });
        setSettledQ(q);
        lastRef.current = { q, region: regionCode, count: list.length, latency: Math.round(performance.now() - started) };
      } catch (e) {
        if ((e as { name?: string })?.name !== 'AbortError') { setItems([]); setSettledQ(q); }
      } finally {
        if (abortRef.current === ac) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(t);
  }, [q, regionCode, limit, debounceMs]);

  // s-log — 입력 멈춤 기록 없음(종전 SUGGEST_SETTLE_MS 뒤 commitLog 제거 · 키 입력 로그 0). 기록은 select(확정) 때만.

  // 선택 = 확정 1행 + 선택(slug·rank)을 «한 요청» 으로 — 곧바로 페이지를 떠나도 keepalive 한 번이면 남는다.
  //   이미 이 질의를 기록했으면(commit) 그 행에 선택만.
  const select = useCallback((slug: string, rank: number) => {
    const last = lastRef.current;
    if (!last || !last.q) return;
    const key = `${last.q}|${last.region ?? ''}`;
    if (loggedRef.current === key && logPromiseRef.current) {
      void logPromiseRef.current.then((id) => { if (id) void post('/api/search/selected', { logId: id, slug, rank }); });
      return;
    }
    loggedRef.current = key;
    const p = post('/api/search/v2-log', {
      q: last.q, count: last.count, latencyMs: last.latency, source, sid: searchSessionId(), region: last.region, slug, rank,
    }).then(async (r) => {
      if (!r || !r.ok) return null;
      const j = await r.json().catch(() => null);
      const id = typeof j?.logId === 'string' ? j.logId : null;
      setLogId(id);
      return id;
    });
    logPromiseRef.current = p;
  }, [source]);

  const empty = !!q && !loading && settledQ === q && items.length === 0;
  return { items, loading, empty, logId, select, commit: commitLog, meta: meta.q === q ? meta : { q, tier: null, zoneNote: null } };
}
